import { Component, inject, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

// External Libs
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleXmark, faStickyNote, faArrowUp, faCircleCheck, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';

// Services
import { DocumentService } from '../../services/document.service';

// Components
import { FileIconComponent } from "../../../shared/components/file-icon/file-icon.component";
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ConfirmOverwriteModalComponent } from '../confirm-overwrite-modal/confirm-overwrite-modal.component';


@Component({
  selector: 'app-file-upload',
  imports: [CommonModule, ReactiveFormsModule, NgxFileDropModule, FontAwesomeModule, FileIconComponent, NgbTooltipModule],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent {
  private readonly docSrv = inject(DocumentService);
  private readonly modalService = inject(NgbModal);
  private readonly activeModal = inject(NgbActiveModal);

  @Input() contextId!: string;

  faArrowUp = faArrowUp;
  faStickyNote = faStickyNote;
  faSpinner = faSpinner;
  faCircleXmark = faCircleXmark; // Icon for pending or default state
  faCircleCheck = faCircleCheck; // Icon for successful upload
  faExclamationTriangle = faExclamationTriangle; // Icon for error or duplicate

  files: File[] = [];
  duplicateFiles: File[] = []; // Track files with 409 conflict error

  errorMessage = '';

  progress: { [key: string]: number } = {}; // Progress for each file
  fileStatus: { [key: string]: 'success' | 'error' | 'duplicate' | 'too-large' | 'pending' | 'uploading' } = {}; // Track file statuses

  acceptedFileTypes = [
    // TXT
    "text/plain",
    // HTML
    "text/html",
    // PDF
    "application/pdf",
    // DOCX
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    // PPTX
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    // XLSX
    // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // XLSM
    // "application/vnd.ms-excel.sheet.macroEnabled.12"
  ];

  private fileQueue: File[] = []; // Queue to manage files
  private uploading = false; // To track if a file is being uploaded

  private currentUploadSub: Subscription | null = null; // Subscription corrente per poter interrompere l’upload

  dropped(files: NgxFileDropEntry[]) {
    this.files = []; // Reset files
    this.progress = {};
    this.fileStatus = {};

    const MAX_SIZE_BYTES = 200 * 1024 * 1024; // 200MB

    files.forEach((droppedFile, index) => {
      const isLastFile = index === files.length - 1;

      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {

          // ▶️ Controllo formato
          if (!this.isAcceptedFileType(file)) {
            console.log(`❌ File type not accepted: ${file.name}`);
          }

          // ▶️ Controllo dimensione
          else if (file.size > MAX_SIZE_BYTES) {
            console.log(
              `❌ File too large (${(file.size / 1024 / 1024).toFixed(2)}MB): ${file.name}`
            );

            // 👇 Aggiungo comunque il file alla lista visibile
            this.files.push(file);

            // 👇 Stato = error
            this.fileStatus[file.name] = 'too-large';

            // 👇 Progress fermo a 0
            this.progress[file.name] = 0;
          }

          // ▶️ OK → aggiungo alla coda
          else {
            this.files.push(file);
            this.fileQueue.push(file);
            this.progress[file.name] = 0;
            this.fileStatus[file.name] = 'pending';
          }

          // ▶️ Se è l’ultimo file → fai partire la queue
          if (isLastFile) {
            this.processQueue();
          }
        });
      } else {
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        console.log(droppedFile.relativePath, fileEntry);
      }
    });
  }


  removeFile(file: File) {
    this.files = this.files.filter(el => el !== file);
    this.fileQueue = this.fileQueue.filter(el => el !== file);
    this.duplicateFiles = this.duplicateFiles.filter(el => el !== file); // Also remove from duplicates list if present
    delete this.progress[file.name];
    delete this.fileStatus[file.name];
  }

  convertToKB(sizeInBytes: number): number {
    return Math.round((sizeInBytes / 1024) * 100) / 100; // Round to two decimal places
  }

  splitFileName(fileName: string): [string, string] {
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return [fileName, '']; // No extension
    }

    const nameWithoutExtension = fileName.slice(0, lastDotIndex);
    const extension = fileName.slice(lastDotIndex + 1);

    return [nameWithoutExtension, extension];
  }

  closeModal() {
    if (this.uploading) {
      this.openConfirmClose()
    } else {
      this.activeModal.dismiss('Cross click');
    }
  }

  private openConfirmClose() {
    const modalRef = this.modalService.open(ConfirmModal, { centered: true, windowClass: 'qai-modal' });
    modalRef.componentInstance.title = $localize`Upload in corso...`;
    modalRef.componentInstance.body = $localize`Ci sono operazioni di upload ancora in corso. Chiudendo la finestra, tali operazioni verranno interrotte.`;
    modalRef.result.then(
      (result: { motivation: string }) => {
        this.cancelUpload();
        this.activeModal.dismiss('Cross click');
      },
      (reason) => {
        console.debug(`Dismissed`);
      },
    );
  }

  private isAcceptedFileType(file: File): boolean {
    return this.acceptedFileTypes.includes(file.type);
  }

  private processQueue() {
    if (this.uploading || this.fileQueue.length === 0) {
      if (this.fileQueue.length === 0) {
        this.promptForDuplicates(); // Prompt for duplicates when all non-duplicates are uploaded
      }
      return; // Skip if already uploading or no files in queue
    }

    const file = this.fileQueue[0]; // Get the first file in the queue
    this.uploadFile(file);
  }

  private uploadFile(file: File) {
    this.uploading = true;
    this.fileStatus[file.name] = 'uploading'; // Set status to "uploading"

    // 🔹 Salvo la subscription così posso interrompere la richiesta HTTP
    this.currentUploadSub = this.docSrv.uploadFile(file, this.contextId).subscribe({
      next: (response) => {
        this.progress[file.name] = response.progress;

        if (response.progress === 100 && response.status === 200) {
          this.fileStatus[file.name] = 'success'; // Mark file as successfully uploaded
          this.completeFileUpload(); // Complete the file upload and move to the next in queue
        }
      },
      error: (error) => {
        if (error.status === 409) { // Handle conflict error
          this.fileStatus[file.name] = 'duplicate'; // Mark file as duplicate
          this.duplicateFiles.push(file); // Add to duplicates list
          this.completeFileUpload(); // Skip and continue processing queue
        } else {
          this.fileStatus[file.name] = 'error'; // Mark file as error
          console.error(`Errore nell'upload di ${file.name}:`, error);
          this.completeFileUpload(); // Remove file from queue on error
        }
      }
    });
  }

  // 🔹 Metodo per interrompere manualmente l’upload
  public cancelUpload() {
    if (this.currentUploadSub) {
      this.currentUploadSub.unsubscribe(); // 💥 Interrompe la richiesta HTTP
      this.currentUploadSub = null;
    }
    this.uploading = false; // Reset flag upload
  }


  private completeFileUpload() {
    this.uploading = false;
    this.fileQueue.shift(); // Remove the uploaded file from the queue
    this.processQueue(); // Start the next upload if there are remaining files
  }

  private promptForDuplicates() {
    // Process each duplicate file individually with overwrite option
    if (this.duplicateFiles.length === 0) return;

    const handleNextDuplicate = () => {
      if (this.duplicateFiles.length === 0) return; // Exit if no duplicates remain

      const file = this.duplicateFiles.shift(); // Remove first duplicate from the list
      if (!file) return;

      const modalRef = this.modalService.open(ConfirmOverwriteModalComponent, { centered: true });
      modalRef.componentInstance.file = file;

      modalRef.result.then((overwrite: boolean) => {
        if (overwrite) {
          this.overwriteFile(file);
        } else {
          this.removeFile(file); // Remove from files list if not overwriting
        }
        handleNextDuplicate(); // Process the next duplicate
      }).catch(() => {
        handleNextDuplicate(); // Continue even if modal was dismissed
      });
    };

    handleNextDuplicate(); // Start handling duplicates
  }

  private overwriteFile(file: File) {
    this.fileStatus[file.name] = 'uploading'; // Set status to "uploading"
    this.docSrv.uploadFile(file, this.contextId, true).subscribe({
      next: (response) => {
        this.progress[file.name] = response.progress;
        if (response.progress === 100) {
          this.fileStatus[file.name] = 'success'; // Mark as successfully overwritten
        }
      },
      error: (error) => {
        this.fileStatus[file.name] = 'error'; // Mark overwrite as error if failed
        console.error(`Errore nell'overwrite di ${file.name}:`, error);
      }
    });
  }
}
